import * as CL from '@design-system/component-library';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { closeMsg, continueMsg, t } from '../../common/i18n/index.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { DialogWrapper } from '../DialogWrapper/index.js';
import type { CommonError } from '../../common/types/errors.js';
import type { AccountHeader } from '../../generated/api/models.js';
import type { DialogWrapperButton } from '../DialogWrapper/index.js';

import './SelectPrimaryAccountDialog.scss';

export interface SelectPrimaryAccountDialogProps {
  isLoading?: boolean;
  errors?: CommonError[];
  accounts: AccountHeader[];
  onSelectPrimaryAccount: (selected: AccountHeader) => void;
  onCloseDialog: () => void;
}

interface FormValues {
  selectedAccountMasterId: string;
}

export const SelectPrimaryAccountDialog = ({
  isLoading,
  errors,
  accounts,
  onSelectPrimaryAccount,
  onCloseDialog,
}: SelectPrimaryAccountDialogProps) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      selectedAccountMasterId: '',
    },
  });

  const selectedAccountMasterId = methods.watch('selectedAccountMasterId');
  const selectedAccount = accounts.find(acc => acc.accountMasterId === selectedAccountMasterId);

  const buttons: DialogWrapperButton[] = [
    errors
      ? {
          onClick: () => onCloseDialog(),
          text: t.WOYD(closeMsg),
        }
      : {
          disabled: selectedAccount === undefined,
          id: 'select-primary-account-dialog-continue',
          isLoading: isLoading,
          onClick: () => {
            if (selectedAccount) {
              onSelectPrimaryAccount(selectedAccount);
            }
          },
          text: t.I62A(continueMsg),
        },
  ];

  return (
    <div className="of-select-primary-account">
      <DialogWrapper
        buttons={buttons}
        closeable={Boolean(errors)}
        header={t.XIFY('Please select your employer company from the list')}
        onCloseDialog={onCloseDialog}
      >
        <div className={`${dsClass.MARGIN_TOP_2} ${dsClass.MARGIN_BOTTOM_4}`}>
          {t.NERD(
            `Your choice only affects which company's user list your information will appear on. You can manage companies according to your user rights.`
          )}
        </div>
        <FormProvider {...methods}>
          <form>
            <div className="of-select-primary-account__options">
              <SelectRadio
                name="selectedAccountMasterId"
                items={accounts.map(account => ({
                  label: `${account.accountName}, ${account.businessId}`,
                  value: account.accountMasterId,
                }))}
                required={false}
              />
            </div>
          </form>
        </FormProvider>
        {errors &&
          errors.map((error, i) => (
            <CL.Disclaimer
              className={dsClass.TEXT_M}
              icon={<CL.Icon icon="warning" />}
              key={i}
              text={error.message}
              visible
            />
          ))}
      </DialogWrapper>
    </div>
  );
};
